/**
 * Cell encoding per BeeNexa Trackers v4 spec § 5.
 *
 *   future                                  — month not yet reached
 *   na                                      — not applicable (quarterly off-month)
 *   pending:AMT:                            — expected payment, not yet paid
 *   overdue:AMT:                            — past due
 *   paid:AMT:DATE                           — paid (manual, unverified)
 *   paid:AMT:DATE:1                         — paid + verified
 *   paid:AMT:DATE:1:SALES                   — sales-tax cell with sales reported
 *   multi:EXP:p1amt~p1date[~p1due][:1];p2amt~p2date[~p2due] — multiple partial payments
 *
 * parseCell is the single source of truth — every UI helper reads through it.
 */

export const parseCell = (raw) => {
    const str = (raw || 'future').toString();
    if (str === 'future' || str === '') {
        return { type: 'future', expected: 0, payments: [] };
    }
    if (str === 'na') {
        return { type: 'na', expected: 0, payments: [] };
    }
    if (str.startsWith('pending:')) {
        const parts = str.split(':');
        return { type: 'pending', expected: Number(parts[1]) || 0, payments: [] };
    }
    if (str.startsWith('overdue:')) {
        const parts = str.split(':');
        return { type: 'overdue', expected: Number(parts[1]) || 0, payments: [] };
    }
    if (str.startsWith('multi:')) {
        const [, exp, ...rest] = str.split(':');
        const tail = rest.join(':');
        const payments = (tail || '')
            .split(';')
            .filter(Boolean)
            .map((seg) => {
                // amt~date[~dueDate][:1[:sales]]
                const [amtDate, verifiedFlag, salesStr] = seg.split(':');
                const [amt, date, dueDate] = (amtDate || '').split('~');
                return {
                    amt: Number(amt) || 0,
                    date: date || '',
                    dueDate: dueDate || '',
                    verified: verifiedFlag === '1',
                    sales: salesStr ? Number(salesStr) || 0 : undefined,
                };
            });
        return { type: 'multi', expected: Number(exp) || 0, payments };
    }
    if (str.startsWith('paid:')) {
        const parts = str.split(':');
        const amt = Number(parts[1]) || 0;
        const date = parts[2] || '';
        const verified = parts[3] === '1';
        const sales = parts[4] ? Number(parts[4]) || 0 : undefined;
        return {
            type: 'paid',
            expected: amt,
            payments: [{ amt, date, verified, sales }],
        };
    }
    return { type: 'future', expected: 0, payments: [] };
};

export const serializeCell = (parsed) => {
    if (!parsed) return 'future';
    const { type, expected = 0, payments = [] } = parsed;
    if (type === 'future') return 'future';
    if (type === 'na') return 'na';
    if (type === 'pending') return `pending:${expected}:`;
    if (type === 'overdue') return `overdue:${expected}:`;
    // dueDate is only encodable in the multi format, so force-promote if any
    // payment carries a dueDate.
    const hasDueDate = payments.some((p) => p.dueDate);
    if (type === 'paid' && payments.length === 1 && !hasDueDate) {
        const p = payments[0];
        const verifiedFlag = p.verified ? '1' : '';
        const salesPart = p.sales != null && p.sales > 0 ? `:${p.sales}` : '';
        // paid:AMT:DATE[:1[:SALES]]
        if (verifiedFlag || salesPart) {
            return `paid:${p.amt}:${p.date}:${verifiedFlag || ''}${salesPart}`;
        }
        return `paid:${p.amt}:${p.date}`;
    }
    if (type === 'multi' || type === 'paid') {
        const segs = payments
            .map((p) => {
                let s = `${p.amt}~${p.date}`;
                if (p.dueDate) s += `~${p.dueDate}`;
                if (p.verified) s += ':1';
                if (p.sales != null && p.sales > 0) {
                    if (!p.verified) s += ':';
                    s += `:${p.sales}`;
                }
                return s;
            })
            .join(';');
        return `multi:${expected}:${segs}`;
    }
    return 'future';
};

export const getCellTotalPaid = (parsed) =>
    (parsed.payments || []).reduce((sum, p) => sum + (Number(p.amt) || 0), 0);

export const getCellTotalSales = (parsed) =>
    (parsed.payments || []).reduce(
        (sum, p) => sum + (Number(p.sales) || 0),
        0,
    );

export const getCellTotalVerified = (parsed) =>
    (parsed.payments || [])
        .filter((p) => p.verified)
        .reduce((sum, p) => sum + (Number(p.amt) || 0), 0);

export const getCellBalance = (parsed) =>
    Math.max(0, (parsed.expected || 0) - getCellTotalPaid(parsed));

export const isCellFullyVerified = (parsed) =>
    (parsed.payments || []).length > 0 &&
    parsed.payments.every((p) => p.verified);

/**
 * Resolve the display status. Future / N/A pass through; otherwise
 * compares total paid vs expected and falls back to overdue/pending.
 */
export const getCellStatus = (parsed) => {
    if (!parsed || parsed.type === 'future') return 'future';
    if (parsed.type === 'na') return 'na';
    const totalPaid = getCellTotalPaid(parsed);
    const expected = parsed.expected || 0;
    if (totalPaid > 0 && totalPaid >= expected) return 'paid';
    if (totalPaid > 0 && totalPaid < expected) return 'partial';
    if (parsed.type === 'overdue') return 'overdue';
    if (parsed.type === 'pending') return 'pending';
    return 'pending';
};

/**
 * Detect Auto-pay vs Manual from the row's paymentMethod field.
 *   'Manual'              → { method: 'Manual', day: null, isAuto: false }
 *   'Auto-pay · day 5'    → { method: 'Auto-pay', day: 5, isAuto: true }
 *   'ACH · day 15'        → { method: 'ACH', day: 15, isAuto: true }
 */
export const parsePaymentMethod = (text) => {
    const raw = (text || 'Manual').toString();
    if (raw.toLowerCase().startsWith('manual')) {
        return { method: 'Manual', day: null, isAuto: false };
    }
    const m = raw.match(/^([\w-]+)\s*[·•]\s*day\s*(\d+)/i);
    if (m) {
        return { method: m[1], day: Number(m[2]), isAuto: true };
    }
    return { method: raw, day: null, isAuto: false };
};

/** Quarterly trackers only fire on Jan/Apr/Jul/Oct (mi=0,3,6,9). */
export const isQuarterlyFilingMonth = (monthIndex) =>
    [0, 3, 6, 9].includes(monthIndex);

export const formatMoney = (amount) => {
    const n = Number(amount) || 0;
    return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

/** Today's date as YYYY-MM-DD — used as a default for new payments. */
export const todayIso = () => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
};
